import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GenomicsCoverPage = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handlePrint = () => {
    window.print();
  };

  const saveToMarketing = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/marketing/ads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'P.O.S - Genomics Partner Cover',
          platform: 'Point of Sale Material',
          type: 'Printable PDF - A4',
          image_url: '/genomics-cover',
          notes: 'One-page branded cover document for genomic companies. Print to PDF from browser.',
        }),
      });
      if (response.ok) {
        setSaved(true);
        alert('✅ Saved to Admin Marketing → P.O.S Materials!');
      }
    } catch (error) {
      alert('Error saving. Please try again.');
    }
    setSaving(false);
  };

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page { 
            margin: 0; 
            padding: 0;
            box-shadow: none !important;
          }
          body { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @page {
            size: A4;
            margin: 0;
          }
        }
      `}</style>

      {/* Control Bar - Hidden on Print */}
      <div className="no-print bg-gray-100 p-4 border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="text-[#1a4d2e] hover:underline"
          >
            ← Back to Home
          </button>
          <div className="flex items-center gap-4">
            <span className="text-gray-600 text-sm">Genomics Partner Cover Document</span>
            <button 
              onClick={saveToMarketing}
              disabled={saving || saved}
              className={`px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 ${
                saved ? 'bg-green-500 text-white' : 'bg-orange-500 text-white hover:bg-orange-600'
              }`}
            >
              {saved ? '✓ Saved to P.O.S' : saving ? 'Saving...' : '💾 Save to Marketing'}
            </button>
            <button 
              onClick={handlePrint}
              className="bg-[#1a4d2e] text-white px-6 py-2 rounded-lg hover:bg-[#2d5a3d] font-semibold flex items-center gap-2"
            >
              <span>🖨️</span> Print / Save as PDF
            </button>
          </div>
        </div>
      </div>

      {/* PDF Document - A4 Size */}
      <div className="flex justify-center p-8 no-print">
        <div 
          className="print-page bg-white shadow-2xl"
          style={{ width: '210mm', minHeight: '297mm', maxHeight: '297mm', overflow: 'hidden' }}
        >
          <DocumentContent />
        </div>
      </div>

      {/* Print Version */}
      <div className="hidden print:block">
        <DocumentContent />
      </div>
    </>
  );
};

const DocumentContent = () => (
  <div className="h-full flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
    
    {/* Header */}
    <div className="bg-[#1a4d2e] text-white px-8 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">MEASURED LIVESTOCK</h1>
          <p className="text-white/70 text-sm mt-1">Data Integrity for Genomics</p>
        </div>
        <div className="text-right">
          <p className="text-white/80 text-xs">measuredlivestock.com</p>
          <p className="text-white/60 text-xs">Manawatu, New Zealand</p>
        </div>
      </div>
    </div>

    {/* Hero Section */}
    <div className="bg-gradient-to-r from-[#1a4d2e] to-[#2d6b45] text-white px-8 py-5">
      <h2 className="text-xl font-semibold mb-2">A Trusted Data Integrity Layer for Genomics</h2>
      <p className="text-white/90 text-sm leading-relaxed">
        High-quality genomic outcomes depend on high-quality data. Measured Livestock acts as a 
        <span className="font-semibold"> data integrity layer </span> 
        between the farm and genomic providers, ensuring that data captured at source is accurate, 
        consistent, and fit for purpose before it enters genomic pipelines.
      </p>
    </div>

    {/* Main Content */}
    <div className="flex-1 px-8 py-4">
      
      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-6">
        
        {/* Left Column */}
        <div className="space-y-4">
          
          {/* Data Quality Commitment */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-[#1a4d2e] rounded-full flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
              <h3 className="font-bold text-[#1a4d2e] text-sm">Data Quality & Integrity</h3>
            </div>
            <ul className="text-xs text-gray-700 space-y-1 ml-8">
              <li>• Accurate alignment of EIDs, Visual IDs & Sample IDs</li>
              <li>• Real-time validation at point of capture</li>
              <li>• Reduction in submission errors & resubmissions</li>
              <li>• Consistent, provider-agnostic data standards</li>
              <li>• Clear traceability from animal to sample to result</li>
            </ul>
          </div>

          {/* Technology */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-[#1a4d2e] rounded-full flex items-center justify-center">
                <span className="text-white text-xs">⚙</span>
              </div>
              <h3 className="font-bold text-[#1a4d2e] text-sm">Technology Enablement</h3>
            </div>
            <ul className="text-xs text-gray-700 space-y-1 ml-8">
              <li>• Industry-standard weighing and data systems</li>
              <li>• DNA Tissue sampling equipment</li>
              <li>• NAIT-compatible identification processes</li>
              <li>• Digital capture workflows for on-farm realities</li>
            </ul>
            <p className="text-xs text-gray-500 italic ml-8 mt-1">
              Technology supports accuracy, not complexity.
            </p>
          </div>

          {/* Security */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-[#1a4d2e] rounded-full flex items-center justify-center">
                <span className="text-white text-xs">🔒</span>
              </div>
              <h3 className="font-bold text-[#1a4d2e] text-sm">Security & Confidentiality</h3>
            </div>
            <ul className="text-xs text-gray-700 space-y-1 ml-8">
              <li>• Secure digital systems with limited access</li>
              <li>• Physical and digital safeguards maintained</li>
              <li>• Farmer consent embedded in data capture</li>
              <li>• No exclusivity or ownership of farmer data required</li>
            </ul>
          </div>

        </div>

        {/* Right Column */}
        <div className="space-y-4">
          
          {/* Value for Genomic Providers */}
          <div className="bg-[#f0f7f2] rounded-lg p-4 border border-[#1a4d2e]/20">
            <h3 className="font-bold text-[#1a4d2e] text-sm mb-2 flex items-center gap-2">
              <span>🧬</span> Value for Genomic Providers
            </h3>
            <ul className="text-xs text-gray-700 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-[#1a4d2e] font-bold">→</span>
                <span>Reduce cost per usable sample</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#1a4d2e] font-bold">→</span>
                <span>Increase data depth and reliability</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#1a4d2e] font-bold">→</span>
                <span>Improve submission quality without exclusivity</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#1a4d2e] font-bold">→</span>
                <span>Maintain provider independence & IP ownership</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#1a4d2e] font-bold">→</span>
                <span>Focus resources on analysis, not data collection</span>
              </li>
            </ul>
          </div>

          {/* Value for Farmers */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="font-bold text-[#1a4d2e] text-sm mb-2 flex items-center gap-2">
              <span>🌾</span> Value for Farmers
            </h3>
            <ul className="text-xs text-gray-700 space-y-1">
              <li>• Greater accuracy & significant time savings</li>
              <li>• Fewer errors and less rework</li>
              <li>• Higher-quality, usable data</li>
              <li>• Better-informed breeding decisions</li>
              <li>• Access to any genomic provider without lock-in</li>
            </ul>
          </div>

          {/* Industry Collaboration */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-[#1a4d2e] rounded-full flex items-center justify-center">
                <span className="text-white text-xs">🤝</span>
              </div>
              <h3 className="font-bold text-[#1a4d2e] text-sm">Industry Collaboration</h3>
            </div>
            <ul className="text-xs text-gray-700 space-y-1 ml-8">
              <li>• Improve submission accuracy & efficiency</li>
              <li>• Align data standards across providers</li>
              <li>• Support ongoing improvement of genetic models</li>
              <li>• Promote evidence-based breeding & selection</li>
              <li>• Contribute to long-term industry sustainability</li>
            </ul>
          </div>

        </div>
      </div>

      {/* Key Message Banner */}
      <div className="mt-4 bg-gradient-to-r from-[#1a4d2e] to-[#2d6b45] rounded-lg p-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-sm">Clean Data. First Time. Every Time.</p>
            <p className="text-white/80 text-xs mt-1">
              Genomic providers receive accurate, validated data without downstream rework.
            </p>
          </div>
          <div className="text-right">
            <p className="text-white/70 text-xs">Provider-agnostic</p>
            <p className="text-white/70 text-xs">No exclusivity required</p>
          </div>
        </div>
      </div>

    </div>

    {/* Footer */}
    <div className="bg-gray-100 px-8 py-4 mt-auto">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-bold text-[#1a4d2e] text-sm">Let's Talk Partnership</p>
          <p className="text-gray-600 text-xs">Contact us to discuss how we can support your genomic data needs.</p>
        </div>
        <div className="text-right text-xs text-gray-600">
          <p className="font-semibold text-[#1a4d2e]">measuredlivestock.com</p>
          <p>info@measuredlivestock.co.nz</p>
          <p>Manawatu, New Zealand</p>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-300 flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Trusted · Independent · Farmer-Approved
        </p>
        <p className="text-xs text-gray-400">
          © {new Date().getFullYear()} Measured Livestock
        </p>
      </div>
    </div>

  </div>
);

export default GenomicsCoverPage;
